import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, CheckCircle } from 'lucide-react';

const businessTypes = [
  'Healthcare & Aesthetics',
  'Education & Coaching',
  'Real Estate',
  'E-commerce',
  'Professional Services',
  'Local Business',
  'Startup / SME',
  'Other',
];

export const LeadForm = ({ source = 'contact_form', onSuccess, compact = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business_type: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleBusinessTypeChange = (value) => {
    setFormData((prev) => ({ ...prev, business_type: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) return 'Please enter your name';
    if (!formData.email.trim()) return 'Please enter your email';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Please enter a valid email';
    if (!formData.phone.trim()) return 'Please enter your phone number';
    if (!/^[\d\s\-+()]{10,}$/.test(formData.phone.replace(/\s/g, ''))) return 'Please enter a valid phone number';
    if (!formData.business_type) return 'Please select your business type';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Submit to PHP endpoint (works on static hosting)
      const response = await fetch('/api/send_mail.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          source,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          business_type: '',
          message: '',
        });
        if (onSuccess) onSuccess();
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Unable to submit form. Please try again or contact us directly at hello@digitalstartup.in');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-8" data-testid="form-success">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">Thank You!</h3>
        <p className="text-[#525252]">
          We've received your inquiry and will get back to you within 24 hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form">
      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm" data-testid="form-error">
          {error}
        </div>
      )}

      <div className={compact ? 'grid grid-cols-2 gap-4' : 'space-y-4'}>
        <div>
          <Label htmlFor="name" className="text-[#1A1A1A] font-medium">
            Full Name *
          </Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="John Doe"
            className="mt-1.5 border-gray-200 focus:border-[#FFAA17] focus:ring-[#FFAA17]"
            data-testid="input-name"
          />
        </div>

        <div>
          <Label htmlFor="email" className="text-[#1A1A1A] font-medium">
            Email Address *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@company.com"
            className="mt-1.5 border-gray-200 focus:border-[#FFAA17] focus:ring-[#FFAA17]"
            data-testid="input-email"
          />
        </div>
      </div>

      <div className={compact ? 'grid grid-cols-2 gap-4' : 'space-y-4'}>
        <div>
          <Label htmlFor="phone" className="text-[#1A1A1A] font-medium">
            Phone Number *
          </Label>
          <Input
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+91 98765 43210"
            className="mt-1.5 border-gray-200 focus:border-[#FFAA17] focus:ring-[#FFAA17]"
            data-testid="input-phone"
          />
        </div>

        <div>
          <Label htmlFor="business_type" className="text-[#1A1A1A] font-medium">
            Business Type *
          </Label>
          <Select value={formData.business_type} onValueChange={handleBusinessTypeChange}>
            <SelectTrigger className="mt-1.5 border-gray-200 focus:border-[#FFAA17] focus:ring-[#FFAA17]" data-testid="select-business-type">
              <SelectValue placeholder="Select your industry" />
            </SelectTrigger>
            <SelectContent>
              {businessTypes.map((type) => (
                <SelectItem key={type} value={type} className="hover:bg-[#FFAA17]/5 hover:text-[#FFAA17] cursor-pointer">
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!compact && (
        <div>
          <Label htmlFor="message" className="text-[#1A1A1A] font-medium">
            Message (Optional)
          </Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your business goals..."
            rows={4}
            className="mt-1.5 border-gray-200 focus:border-[#FFAA17] focus:ring-[#FFAA17]"
            data-testid="input-message"
          />
        </div>
      )}

      <Button
        type="submit"
        disabled={isLoading}
        className="w-full bg-[#FFAA17] hover:bg-[#E69500] text-[#1A1A1A] font-semibold rounded-full py-3 shadow-lg shadow-amber-500/20 hover:shadow-amber-500/30 transition-all"
        data-testid="form-submit"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          'Get Free Growth Audit'
        )}
      </Button>

      <p className="text-xs text-center text-[#525252]">
        By submitting, you agree to our privacy policy. We'll never spam you.
      </p>
    </form>
  );
};

export default LeadForm;
